import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from "react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Briefcase,
  GraduationCap,
  Linkedin,
  Mail,
  type LucideIcon,
} from "lucide-react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";
import { fallbackPortfolioData, type CvDetails, type CvSection } from "./data/portfolio";
import { activateFirebaseAnalytics } from "./lib/firebase";
import { loadPortfolioData } from "./lib/portfolioStore";

const heroRoutes: {
  href: string;
  label: string;
  Icon?: LucideIcon;
  marker?: "circle";
}[] = [
  {
    href: "#education",
    label: "Education",
    Icon: GraduationCap,
  },
  {
    href: "#research",
    label: "Research",
    Icon: BookOpen,
  },
  {
    href: "#experience",
    label: "Experience",
    Icon: Briefcase,
  },
  {
    href: "/cv",
    label: "CV",
    Icon: FileText,
  },
];

const jumpDots = [
  { className: "dot-one", x: -46, y: -34 },
  { className: "dot-two", x: 44, y: -28 },
  { className: "dot-three", x: -30, y: 42 },
  { className: "dot-four", x: 42, y: 36 },
];

function getSectionScrollTarget(section: HTMLElement) {
  return (
    section.querySelector<HTMLElement>(
      ".section-heading, .education-panel, .cv-dossier, .contact-panel",
    ) ?? section
  );
}

function scrollToElement(element: HTMLElement) {
  const top = element.getBoundingClientRect().top + window.scrollY - 28;
  const scrollTop = Math.max(top, 0);

  window.scrollTo({
    top: scrollTop,
    behavior: "auto",
  });
  document.documentElement.scrollTop = scrollTop;
  document.body.scrollTop = scrollTop;

  window.requestAnimationFrame(() => {
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);
  });
}

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      className={className}
      initial={false}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <Reveal className={children ? "section-heading" : "section-heading section-heading-solo"}>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </Reveal>
  );
}

function isCvRoute() {
  if (typeof window === "undefined") {
    return false;
  }

  const pathname = window.location.pathname.replace(/\/$/, "");
  return pathname === "/cv" || window.location.hash === "#cv";
}

function CvDossier({
  activeCvSection,
  className = "",
  cv,
  onSectionSelect,
  shouldReduceMotion,
}: {
  activeCvSection?: CvSection;
  className?: string;
  cv: CvDetails;
  onSectionSelect: (sectionId: string) => void;
  shouldReduceMotion: boolean | null;
}) {
  const swipeStartRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);
  const wheelResetRef = useRef<number | undefined>(undefined);
  const wheelCooldownRef = useRef(0);
  const wheelIntentRef = useRef({ x: 0, y: 0 });
  const activeCvSectionIndex = activeCvSection
    ? cv.sections.findIndex((section) => section.id === activeCvSection.id)
    : -1;
  const hasPreviousSection = activeCvSectionIndex > 0;
  const hasNextSection = activeCvSectionIndex >= 0 && activeCvSectionIndex < cv.sections.length - 1;

  const selectSectionByIndex = (sectionIndex: number) => {
    const nextSection = cv.sections[sectionIndex];

    if (!nextSection || sectionIndex === activeCvSectionIndex) {
      return false;
    }

    onSectionSelect(nextSection.id);
    return true;
  };

  const selectAdjacentSection = (direction: -1 | 1) => {
    return selectSectionByIndex(activeCvSectionIndex + direction);
  };

  const handleSwipeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.isPrimary === false) {
      return;
    }

    const target = event.target as HTMLElement;

    if (target.closest("a, button")) {
      return;
    }

    swipeStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  };

  const handleSwipeEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;

    if (!start || start.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const horizontalDistance = Math.abs(deltaX);
    const verticalDistance = Math.abs(deltaY);

    if (horizontalDistance < 58 || horizontalDistance < verticalDistance * 1.35) {
      return;
    }

    selectAdjacentSection(deltaX < 0 ? 1 : -1);
  };

  const handleTrackpadWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const horizontalDistance = Math.abs(event.deltaX);
    const verticalDistance = Math.abs(event.deltaY);

    if (horizontalDistance < 8 || horizontalDistance < verticalDistance * 1.18) {
      return;
    }

    event.preventDefault();

    const now = window.performance.now();

    if (now < wheelCooldownRef.current) {
      return;
    }

    wheelIntentRef.current = {
      x: wheelIntentRef.current.x + event.deltaX,
      y: wheelIntentRef.current.y + event.deltaY,
    };

    if (wheelResetRef.current) {
      window.clearTimeout(wheelResetRef.current);
    }

    wheelResetRef.current = window.setTimeout(() => {
      wheelIntentRef.current = { x: 0, y: 0 };
    }, 140);

    const intentX = Math.abs(wheelIntentRef.current.x);
    const intentY = Math.abs(wheelIntentRef.current.y);

    if (intentX < 72 || intentX < intentY * 1.18) {
      return;
    }

    if (selectAdjacentSection(wheelIntentRef.current.x > 0 ? 1 : -1)) {
      wheelCooldownRef.current = now + 760;
      wheelIntentRef.current = { x: 0, y: 0 };
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const target = event.target;

      if (
        target instanceof HTMLElement &&
        target.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        selectAdjacentSection(1);
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        selectAdjacentSection(-1);
        return;
      }

      const sectionNumber = Number.parseInt(event.key, 10);

      if (
        Number.isInteger(sectionNumber) &&
        sectionNumber >= 1 &&
        sectionNumber <= cv.sections.length
      ) {
        event.preventDefault();
        selectSectionByIndex(sectionNumber - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);

      if (wheelResetRef.current) {
        window.clearTimeout(wheelResetRef.current);
      }
    };
  });

  return (
    <motion.div
      className={["cv-dossier", className].filter(Boolean).join(" ")}
      initial={false}
      onPointerCancel={() => {
        swipeStartRef.current = null;
      }}
      onPointerDown={handleSwipeStart}
      onPointerUp={handleSwipeEnd}
      onWheel={handleTrackpadWheel}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="cv-dossier-backdrop" aria-hidden="true" />
      <motion.div
        className="cv-dossier-head"
        initial={shouldReduceMotion ? false : { opacity: 0, y: 34 }}
        whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <h2>{cv.title}</h2>
        </div>
        <div className="cv-actions">
          {cv.previewUrl ? (
            <a href={cv.previewUrl} target="_blank" rel="noreferrer">
              <Eye aria-hidden="true" />
              <span>{cv.previewLabel}</span>
            </a>
          ) : (
            <button type="button" disabled>
              <Eye aria-hidden="true" />
              <span>{cv.previewLabel}</span>
            </button>
          )}
          {cv.pdfUrl ? (
            <a href={cv.pdfUrl} download>
              <Download aria-hidden="true" />
              <span>{cv.downloadLabel}</span>
            </a>
          ) : (
            <button type="button" disabled>
              <Download aria-hidden="true" />
              <span>{cv.downloadLabel}</span>
            </button>
          )}
        </div>
      </motion.div>

      <div className="cv-dossier-grid">
        <nav className="cv-index-rail" aria-label="CV sections">
          <span>Index</span>
          {cv.sections.map((section, index) => (
            <button
              className={section.id === activeCvSection?.id ? "is-active" : undefined}
              key={section.id}
              onClick={() => onSectionSelect(section.id)}
              type="button"
            >
              <small>{String(index + 1).padStart(2, "0")}</small>
              <strong>{section.title}</strong>
            </button>
          ))}
        </nav>

        <motion.div
          className="cv-reader"
          key={activeCvSection?.id}
          initial={shouldReduceMotion ? false : { opacity: 0, x: 32, rotate: 0.6 }}
          animate={shouldReduceMotion ? undefined : { opacity: 1, x: 0, rotate: 0 }}
          transition={{ duration: 0.82, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="cv-reader-heading">
            <h3>{activeCvSection?.title}</h3>
          </div>
          <div className="cv-entry-stack">
            {activeCvSection?.items.map((item, index) => (
              <motion.article
                className={item.apa ? "cv-entry cv-entry-apa" : "cv-entry"}
                key={`${activeCvSection.id}-${item.title}-${index}`}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 22 }}
                animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
                transition={{
                  duration: 0.68,
                  delay: shouldReduceMotion ? 0 : index * 0.09,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <div className="cv-entry-header">
                  <div>
                    <h4>{item.title}</h4>
                    {item.meta && <p>{item.meta}</p>}
                  </div>
                  {item.date && <span>{item.date}</span>}
                </div>
                {item.citation && (
                  <p className="cv-apa-citation">{item.citation}</p>
                )}
                {item.description && <p>{item.description}</p>}
                {item.bullets && (
                  <ul>
                    {item.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
      <div className="cv-swipe-footer" aria-label="CV section navigation">
        <button
          aria-label="Previous CV section"
          className="cv-swipe-arrow"
          disabled={!hasPreviousSection}
          onClick={() => selectAdjacentSection(-1)}
          title="Previous section"
          type="button"
        >
          <ChevronLeft aria-hidden="true" />
        </button>
        <div className="cv-swipe-cue" aria-hidden="true">
          <span />
        </div>
        <button
          aria-label="Next CV section"
          className="cv-swipe-arrow"
          disabled={!hasNextSection}
          onClick={() => selectAdjacentSection(1)}
          title="Next section"
          type="button"
        >
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
    </motion.div>
  );
}

function App() {
  const shouldReduceMotion = useReducedMotion();
  const [portfolioData, setPortfolioData] = useState(fallbackPortfolioData);
  const [isCvPage, setIsCvPage] = useState(isCvRoute);
  const [activeCvSectionId, setActiveCvSectionId] = useState(
    fallbackPortfolioData.cv.sections[0]?.id ?? "",
  );
  const [jumpBurst, setJumpBurst] = useState<{
    id: number;
    x: number;
    y: number;
    label: string;
  } | null>(null);
  const jumpClearRef = useRef<number | undefined>(undefined);
  const scrollClearRef = useRef<number | undefined>(undefined);
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    mass: 0.3,
  });
  const {
    about,
    awards,
    cv,
    education,
    experiences,
    memberships,
    profile,
    publications,
    seniorProjects,
  } = portfolioData;
  const activeCvSection =
    cv.sections.find((section) => section.id === activeCvSectionId) ?? cv.sections[0];

  const mailHref = useMemo(() => `mailto:${profile.email}`, [profile.email]);

  useEffect(() => {
    void activateFirebaseAnalytics();
  }, []);

  useEffect(() => {
    const syncRoute = () => {
      setIsCvPage(isCvRoute());
    };

    window.addEventListener("hashchange", syncRoute);
    window.addEventListener("popstate", syncRoute);

    return () => {
      window.removeEventListener("hashchange", syncRoute);
      window.removeEventListener("popstate", syncRoute);
    };
  }, []);

  useEffect(() => {
    const pageTitle = isCvPage
      ? `${profile.name} | Curriculum Vitae`
      : "Xander Marshall | Psychology Research Portfolio";
    const pageUrl = isCvPage
      ? "https://xandermarshall.com/cv"
      : "https://xandermarshall.com/";
    const description = isCvPage
      ? "Interactive curriculum vitae for Xander Marshall."
      : "Xander Marshall is a Cal Poly San Luis Obispo psychology student focused on assessment, prevention, intervention, and developmental research.";
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    const twitterTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');

    document.title = pageTitle;
    canonical?.setAttribute("href", pageUrl);
    descriptionMeta?.setAttribute("content", description);
    ogUrl?.setAttribute("content", pageUrl);
    ogTitle?.setAttribute("content", pageTitle);
    ogDescription?.setAttribute("content", description);
    twitterTitle?.setAttribute("content", pageTitle);
    twitterDescription?.setAttribute("content", description);

    window.scrollTo({ top: 0, behavior: "auto" });
  }, [isCvPage, profile.name]);

  useEffect(() => {
    let isMounted = true;

    void loadPortfolioData().then((data) => {
      if (isMounted) {
        setPortfolioData(data);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      cv.sections.length > 0 &&
      !cv.sections.some((section) => section.id === activeCvSectionId)
    ) {
      setActiveCvSectionId(cv.sections[0].id);
    }
  }, [activeCvSectionId, cv.sections]);

  useEffect(() => {
    return () => {
      if (jumpClearRef.current) {
        window.clearTimeout(jumpClearRef.current);
      }

      if (scrollClearRef.current) {
        window.clearTimeout(scrollClearRef.current);
      }
    };
  }, []);

  const handleSectionJump = (
    event: ReactMouseEvent<HTMLAnchorElement>,
    href: string,
    label: string,
  ) => {
    const target = document.querySelector<HTMLElement>(href);

    if (!target) {
      return;
    }

    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const id = window.performance.now();

    if (jumpClearRef.current) {
      window.clearTimeout(jumpClearRef.current);
    }

    if (scrollClearRef.current) {
      window.clearTimeout(scrollClearRef.current);
    }

    setJumpBurst({
      id,
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      label,
    });

    scrollClearRef.current = window.setTimeout(() => {
      const scrollTarget = getSectionScrollTarget(target);
      scrollToElement(scrollTarget);
      window.history.pushState(null, "", href);
    }, shouldReduceMotion ? 0 : 260);

    jumpClearRef.current = window.setTimeout(() => {
      setJumpBurst(null);
    }, 820);
  };

  const handleCvOpen = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsCvPage(true);
    window.history.pushState(null, "", "/cv");
  };

  const handlePortfolioOpen = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsCvPage(false);
    window.history.pushState(null, "", "/");
  };

  if (isCvPage) {
    return (
      <div className="app-shell cv-page-shell">
        <main id="cv" className="cv-page" aria-labelledby="cv-page-title">
          <div className="cv-page-nav">
            <a href="/" onClick={handlePortfolioOpen}>
              <ArrowLeft aria-hidden="true" />
              <span>Portfolio</span>
            </a>
            <p id="cv-page-title">{profile.name} | Curriculum Vitae</p>
          </div>
          <CvDossier
            activeCvSection={activeCvSection}
            className="cv-dossier-fullscreen"
            cv={cv}
            onSectionSelect={setActiveCvSectionId}
            shouldReduceMotion={shouldReduceMotion}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <motion.div className="scroll-progress" style={{ scaleX: smoothProgress }} />
      {jumpBurst && !shouldReduceMotion && (
        <motion.div
          aria-hidden="true"
          className="jump-burst"
          key={jumpBurst.id}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.82, ease: "easeOut" }}
          style={{ left: jumpBurst.x, top: jumpBurst.y }}
        >
          <motion.span
            className="jump-burst-ring"
            initial={{ opacity: 0.68, scale: 0.18 }}
            animate={{ opacity: 0, scale: 22 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.span
            className="jump-burst-core"
            initial={{ scale: 0.8, rotate: -10 }}
            animate={{ scale: 1.35, rotate: 10 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          />
          {jumpDots.map((dot) => (
            <motion.span
              className={`jump-burst-dot ${dot.className}`}
              key={dot.className}
              initial={{ opacity: 0, scale: 0.35, x: 0, y: 0 }}
              animate={{ opacity: [0, 1, 0], scale: [0.35, 1, 0.55], x: dot.x, y: dot.y }}
              transition={{ duration: 0.68, ease: [0.16, 1, 0.3, 1] }}
            />
          ))}
          <motion.span
            className="jump-burst-label"
            initial={{ opacity: 0, y: 8, scale: 0.92 }}
            animate={{ opacity: [0, 1, 0], y: -24, scale: 1 }}
            transition={{ duration: 0.78, ease: [0.16, 1, 0.3, 1] }}
          >
            {jumpBurst.label}
          </motion.span>
        </motion.div>
      )}

      <main id="top">
        <section className="hero-section" aria-labelledby="hero-title">
          <div className="hero-content">
            <Reveal className="hero-copy">
              <h1 id="hero-title">{profile.headline}</h1>
              <p className="hero-statement">{profile.statement}</p>
              <div className="hero-actions">
                <a
                  className="primary-link"
                  href={profile.linkedin}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Linkedin aria-hidden="true" />
                  <span>LinkedIn</span>
                </a>
                <motion.a
                  className="secondary-link"
                  href="/cv"
                  onClick={handleCvOpen}
                  whileTap={shouldReduceMotion ? undefined : { scale: 0.96 }}
                >
                  <Eye aria-hidden="true" />
                  <span>View CV</span>
                </motion.a>
                <a className="secondary-link" href={mailHref}>
                  <Mail aria-hidden="true" />
                  <span>Email</span>
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal className="hero-route-panel hero-shortcuts">
            <div className="route-list" aria-label="Section links">
              {heroRoutes.map((route, index) => {
                const Icon = route.Icon;
                return (
                  <motion.a
                    className={[
                      "route-link",
                      route.marker === "circle" ? "route-link-circle" : "",
                      !Icon && !route.marker ? "route-link-no-icon" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    href={route.href}
                    key={route.href}
                    initial={
                      shouldReduceMotion ? false : { opacity: 0, y: 14 }
                    }
                    animate={
                      shouldReduceMotion ? undefined : { opacity: 1, y: 0 }
                    }
                    transition={{
                      duration: 0.58,
                      delay: 0.18 + index * 0.07,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={shouldReduceMotion ? undefined : { y: -4 }}
                    whileTap={
                      shouldReduceMotion
                        ? undefined
                        : { scale: 0.965, rotate: index % 2 === 0 ? -0.6 : 0.6 }
                    }
                    onClick={(event) => {
                      if (route.href === "/cv") {
                        handleCvOpen(event);
                        return;
                      }

                      handleSectionJump(event, route.href, route.label);
                    }}
                  >
                    {Icon && (
                      <span className="route-icon">
                        <Icon aria-hidden="true" />
                      </span>
                    )}
                    {route.marker === "circle" && <span className="route-open-circle" aria-hidden="true" />}
                    <span>
                      <strong>{route.label}</strong>
                    </span>
                    <ChevronRight aria-hidden="true" />
                  </motion.a>
                );
              })}
            </div>
          </Reveal>
        </section>

        <section id="about" className="content-section about-section">
          <Reveal className="about-panel">
            <div className="about-story">
              <h2>{about.title}</h2>
              {about.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </Reveal>
        </section>

        <section id="education" className="intro-band" aria-label="Education">
          <Reveal className="education-panel">
            <div className="education-main">
              <span className="eyebrow">Education</span>
              <div className="education-heading-row">
                <h2>{education.institution}</h2>
                <span>
                  {education.location} | {education.graduation}
                </span>
              </div>
              <p className="education-degree-line">
                <span>{education.degree}</span>
                <span>GPA: {education.gpa}</span>
              </p>
              <div className="tag-row">
                {education.courses.map((course) => (
                  <span key={course}>{course}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="recognition-band">
            <div>
              <span className="eyebrow">Honors and Memberships</span>
              <ul>
                {awards.map((item) => (
                  <li key={item}>{item}</li>
                ))}
                {memberships.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>

        <section id="research" className="content-section research-section">
          <SectionHeading title="Research" />

          <div className="project-grid">
            {seniorProjects.map((project, index) => (
              <Reveal className="project-card" key={project.title} delay={index * 0.06}>
                <div className="project-card-header">
                  <span>{project.status}</span>
                </div>
                <h3>{project.title}</h3>
                <p>{project.summary}</p>
                <div className="tag-row">
                  {project.methods.map((method) => (
                    <span key={method}>{method}</span>
                  ))}
                </div>
                <ul>
                  {project.outcomes.map((outcome) => (
                    <li key={outcome}>
                      <CheckCircle2 aria-hidden="true" />
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <Reveal className="publication-list compact-publications">
            {publications.map((publication) => (
              <article className="publication-row" key={publication.title}>
                <div className="publication-icon">
                  <FileText aria-hidden="true" />
                </div>
                <div>
                  <span>{publication.status}</span>
                  <h3>{publication.title}</h3>
                  <p>{publication.venue}</p>
                  <p>{publication.contribution}</p>
                </div>
              </article>
            ))}
          </Reveal>
        </section>

        <section id="experience" className="content-section experience-section">
          <SectionHeading title="Experience" />

          <div className="timeline">
            {experiences.map((experience, index) => (
              <Reveal className="timeline-item" key={experience.role} delay={index * 0.05}>
                <div className="timeline-marker" aria-hidden="true" />
                <article>
                  <div className="timeline-header">
                    <span>{experience.dateRange}</span>
                    <h3>{experience.role}</h3>
                    <p>{experience.organization}</p>
                  </div>
                  <p>{experience.summary}</p>
                  <ul>
                    {experience.highlights.map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section id="contact" className="contact-section" aria-labelledby="contact-title">
          <Reveal className="contact-panel">
            <div>
              <h2 id="contact-title">Contact</h2>
              <p>Reach out by email or connect on LinkedIn.</p>
            </div>
            <div className="contact-actions">
              <a
                className="primary-link"
                href={profile.linkedin}
                target="_blank"
                rel="noreferrer"
              >
                <Linkedin aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
              <a className="primary-link" href={mailHref}>
                <Mail aria-hidden="true" />
                <span>{profile.email}</span>
              </a>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="site-footer">
        <p>{profile.name} | Portfolio</p>
        <a href="#top">Back to Top</a>
      </footer>
    </div>
  );
}

export default App;
